import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import type { MentionableEntity } from './RichTextField';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { RichTextField } from './RichTextField';
import { AddItemButton } from './AddItemButton';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { PanelPageHeader } from './PanelPageHeader';
import { PanelHeaderActions } from './PanelHeaderActions';
import { ScenesEmptyState } from './ScenesEmptyState';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent } from './ui/tabs';
import { ViewSwitcher, useViewModeSearchParam } from './ViewSwitcher';
import { SingleSelectCombobox } from './ui/single-select-combobox';
import { MultiSelectCombobox } from './ui/multi-select-combobox';
import { EditDialogFooter } from './EditDialogFooter';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { Plus, Pencil, MoreVertical, GripVertical } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortableSensors } from '../hooks/use-sortable-sensors';
import { SortableDragHandle } from './SortableDragHandle';
import { CSS } from '@dnd-kit/utilities';
import { ScenesTimeline, type ScenesTimelineScene } from './ScenesTimeline';
import { RelatedEntityLink } from './RelatedEntityLink';
import { removeMentionFromHtml, type RelatedEntity } from '../utils/entity-utils';
import { getRelatedEntitiesFor } from '../utils/related-entities';
import { removeSceneOutboundLink } from '../utils/scene-links';
import { useToast } from '../contexts/ToastContext';
import { EntityTagsField, EntityTagBadge } from './EntityTagsField';
import { ConfirmDialog } from './ConfirmDialog';

export interface Scene {
  id: string;
  order: number;
  title: string;
  description?: string;
  documentId?: string;
  documentTitle?: string;
}

interface ScenesProps {
  documentId?: string;
  projectId?: string;
  isSidebar?: boolean;
  onClose?: () => void;
  initialSceneId?: string;
}

interface SortableSceneCardProps {
  scene: Scene;
  index: number;
  onEdit: (scene: Scene) => void;
  onDelete: (scene: Scene) => void;
  deletingSceneId: string | null;
  editingSceneId: string | null;
  token: string | null;
  masterKey: string | null;
  documentId: string | undefined;
  storylines: Array<{ id: string; name: string }>;
  t: (key: string) => string;
}

const SortableSceneCard: React.FC<SortableSceneCardProps> = ({
  scene,
  index,
  onEdit,
  onDelete,
  deletingSceneId,
  editingSceneId,
  token,
  masterKey,
  documentId,
  storylines,
  t,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: scene.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: isDragging ? 'none' : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="min-w-0 bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onEdit(scene)}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <SortableDragHandle attributes={attributes} listeners={listeners}>
            <GripVertical className="h-4 w-4" />
          </SortableDragHandle>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                disabled={deletingSceneId === scene.id}
                aria-label={t('actions')}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onDelete(scene)}
                disabled={deletingSceneId === scene.id}
                className="group text-red-600"
              >
                <RotatingDeleteIcon className="mr-2" />
                {t('delete_scene')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <h3 className="text-lg font-semibold mb-2 text-card-foreground">{scene.title}</h3>
      {scene.description && (
        <div
          className={`text-sm text-muted-foreground mb-3 line-clamp-3 ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
          dangerouslySetInnerHTML={{ __html: scene.description }}
        />
      )}
      {storylines.length > 0 && (
        <div className="flex max-w-full min-w-0 flex-wrap gap-1.5" onClick={(event) => event.stopPropagation()}>
          {storylines.map((storyline) => (
            <EntityTagBadge
              key={storyline.id}
              entityType="storyline"
              id={storyline.id}
              name={storyline.name}
              documentId={documentId}
              className="gap-1 px-2 py-0.5 text-xs"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const Scenes: React.FC<ScenesProps> = ({
  documentId: propDocumentId,
  projectId: propProjectId,
  isSidebar = false,
  onClose,
  initialSceneId,
}) => {
  const params = useParams<{ projectId?: string; documentId: string }>();
  const documentId = propDocumentId || params.documentId;
  const projectId = propProjectId || params.projectId;
  const { token } = useAuth();
  const { t } = useLocale();
  const [masterKey] = useState(() => new URLSearchParams(window.location.search).get('masterKey'));
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSceneOrder, setNewSceneOrder] = useState('');
  const [newSceneTitle, setNewSceneTitle] = useState('');
  const [newSceneDescription, setNewSceneDescription] = useState('');
  const [newSceneStorylineIds, setNewSceneStorylineIds] = useState<string[]>([]);
  const [addingScene, setAddingScene] = useState(false);
  const [editingSceneId, setEditingSceneId] = useState<string | null>(null);
  const [deletingSceneId, setDeletingSceneId] = useState<string | null>(null);
  const [deleteConfirmScene, setDeleteConfirmScene] = useState<Scene | null>(null);
  const [viewMode, setViewMode] = useViewModeSearchParam({
    allowedModes: ['grid', 'table', 'timeline'] as const,
    defaultMode: 'grid',
  });
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [sceneTimelineGroupBy, setSceneTimelineGroupBy] = useState<'none' | 'chapter' | 'character' | 'location'>('none');
  const [sceneTimelineGroupByChapters, setSceneTimelineGroupByChapters] = useState<Set<string>>(new Set());
  const [sceneTimelineGroupByCharacters, setSceneTimelineGroupByCharacters] = useState<Set<string>>(new Set());
  const [sceneTimelineGroupByLocations, setSceneTimelineGroupByLocations] = useState<Set<string>>(new Set());
  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    sourceDocumentId: string | null;
    targetType: string;
    targetId: string;
    targetDocumentId: string | null;
  }>>([]);
  const [storylines, setStorylines] = useState<Array<{ id: string; title: string }>>([]);
  const { showErrorToast, showFieldSavedToast } = useToast();

  useEffect(() => {
    const fetchMentionableEntities = async (): Promise<void> => {
      if (!projectId || (!token && !masterKey)) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    fetchMentionableEntities();
  }, [projectId, token, masterKey]);

  const fetchEntityRefs = useCallback(async (): Promise<void> => {
    if (!projectId || (!token && !masterKey)) return;
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (res.ok) {
        const data = await res.json();
        setEntityRefs(data.references ?? []);
      } else {
        setEntityRefs([]);
      }
    } catch {
      setEntityRefs([]);
    }
  }, [projectId, token, masterKey]);

  useEffect(() => {
    fetchEntityRefs();
  }, [fetchEntityRefs]);

  const fetchStorylines = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !documentId) return;
    try {
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/storylines?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/storylines`;
      const response = await fetch(url, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch storylines');
      const data = await response.json();
      setStorylines(Array.isArray(data.storylines) ? data.storylines : []);
    } catch (err) {
      console.error('Error fetching storylines:', err);
    }
  }, [token, documentId, masterKey]);

  useEffect(() => {
    fetchStorylines();
  }, [fetchStorylines]);

  const storylineOptions = useMemo(
    () => storylines.map((s) => ({ id: s.id, name: s.title, documentId })),
    [storylines, documentId]
  );

  const storylinesById = useMemo(() => new Map(storylines.map((s) => [s.id, s.title])), [storylines]);

  const storylineIdsByScene = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!documentId) return map;
    for (const ref of entityRefs) {
      if (ref.sourceType === 'scene' && ref.sourceDocumentId === documentId && ref.targetType === 'storyline') {
        const existing = map.get(ref.sourceId) ?? [];
        existing.push(ref.targetId);
        map.set(ref.sourceId, existing);
      }
    }
    return map;
  }, [entityRefs, documentId]);

  const selectedStorylineIds = useMemo(
    () => (editingSceneId ? storylineIdsByScene.get(editingSceneId) ?? [] : []),
    [editingSceneId, storylineIdsByScene]
  );

  const handleStorylineTagsChange = useCallback(
    async (nextIds: string[]): Promise<void> => {
      if (!editingSceneId || !documentId || !projectId || !token) return;
      const currentIds = storylineIdsByScene.get(editingSceneId) ?? [];
      const toAdd = nextIds.filter((id) => !currentIds.includes(id));
      const toRemove = currentIds.filter((id) => !nextIds.includes(id));
      if (toAdd.length === 0 && toRemove.length === 0) {
        return;
      }
      try {
        await Promise.all([
          ...toAdd.map((storylineId) =>
            fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                sourceType: 'scene',
                sourceId: editingSceneId,
                sourceDocumentId: documentId,
                targetType: 'storyline',
                targetId: storylineId,
                targetDocumentId: documentId,
              }),
            })
          ),
          ...toRemove.map((storylineId) =>
            fetch(`${API_BASE}/api/projects/${projectId}/entity-references/remove`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                sourceType: 'scene',
                sourceId: editingSceneId,
                sourceDocumentId: documentId,
                targetType: 'storyline',
                targetId: storylineId,
                targetDocumentId: documentId,
              }),
            })
          ),
        ]);
        await fetchEntityRefs();
        showFieldSavedToast(t('storylines'));
      } catch {
        showErrorToast(t('Failed to update storylines'));
      }
    },
    [editingSceneId, documentId, projectId, token, storylineIdsByScene, fetchEntityRefs, t, showErrorToast, showFieldSavedToast]
  );

  const characters = useMemo(
    () => mentionableEntities.filter((e) => e.type === 'character').map((e) => ({ id: e.id, name: e.label })),
    [mentionableEntities]
  );
  const locations = useMemo(
    () => mentionableEntities.filter((e) => e.type === 'location').map((e) => ({ id: e.id, name: e.label })),
    [mentionableEntities]
  );
  const chaptersForScenes = useMemo(
    () => mentionableEntities
      .filter((e) => e.type === 'manual_chapter')
      .map((e) => ({ id: e.id, title: e.label })),
    [mentionableEntities]
  );

  const relatedEntities = useMemo(() => {
    if (!editingSceneId || !documentId) return { linksTo: [], referencedIn: [] };
    return getRelatedEntitiesFor(
      'scene',
      editingSceneId,
      documentId,
      entityRefs,
      mentionableEntities,
    );
  }, [editingSceneId, documentId, entityRefs, mentionableEntities]);

  const fetchScenes = useCallback(async (): Promise<void> => {
    if ((!token && !masterKey) || !documentId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/scenes`;
      const response = await fetch(url, {
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to fetch scenes');
      const data = await response.json();
      setScenes(Array.isArray(data.scenes) ? data.scenes : []);
    } catch (err) {
      console.error('Error fetching scenes:', err);
      setError(t('failed_to_load_scenes'));
    } finally {
      setLoading(false);
    }
  }, [token, documentId, masterKey, t]);

  const handleRemoveLinksTo = useCallback(
    async (entity: RelatedEntity): Promise<void> => {
      if (!editingSceneId || !documentId || !projectId || !token) return;
      try {
        const removed = await removeSceneOutboundLink({
          projectId,
          token,
          sceneId: editingSceneId,
          documentId,
          entity,
        });
        if (!removed) {
          showErrorToast(t('Failed to remove entity reference'));
          return;
        }
        const newDesc = removeMentionFromHtml(newSceneDescription || '', entity);
        const orderNum = parseInt(newSceneOrder.trim(), 10);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}`;
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            order: isNaN(orderNum) ? 1 : orderNum,
            title: newSceneTitle.trim(),
            description: newDesc.trim() || undefined,
          }),
        });
        if (!res.ok) throw new Error('Failed to update');
        setNewSceneDescription(newDesc.trim() || '');
        await fetchScenes();
        await fetchEntityRefs();
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [
      editingSceneId,
      documentId,
      projectId,
      token,
      newSceneDescription,
      newSceneOrder,
      newSceneTitle,
      masterKey,
      fetchScenes,
      fetchEntityRefs,
      t,
      showErrorToast,
    ]
  );

  const handleRemoveReferencedIn = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!token || !projectId || !editingSceneId || !documentId) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceType: entity.type,
            sourceId: entity.id,
            sourceDocumentId: entity.documentId ?? null,
            targetType: 'scene',
            targetId: editingSceneId,
            targetDocumentId: documentId,
          }),
        });
        if (res.ok) {
          await fetchEntityRefs();
        } else {
          showErrorToast(t('Failed to remove entity reference'));
        }
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [editingSceneId, documentId, projectId, token, fetchEntityRefs, t, showErrorToast]
  );

  useEffect(() => {
    fetchScenes();
  }, [fetchScenes]);

  useEffect(() => {
    if (!initialSceneId || loading) return;
    const scene = scenes.find((s) => s.id === initialSceneId);
    if (scene) {
      setEditingSceneId(initialSceneId);
      setNewSceneOrder(scene.order.toString());
      setNewSceneTitle(scene.title);
      setNewSceneDescription(scene.description ?? '');
      setShowAddModal(true);
    }
  }, [initialSceneId, loading, scenes]);

  const sensors = useSortableSensors();

  const closeModal = (): void => {
    setShowAddModal(false);
    setEditingSceneId(null);
    setNewSceneOrder('');
    setNewSceneTitle('');
    setNewSceneDescription('');
    setNewSceneStorylineIds([]);
  };

  useEffect(() => {
    if (!showAddModal || editingSceneId !== null) return;
    const maxOrder = scenes.length > 0 ? Math.max(...scenes.map((s) => s.order)) : 0;
    setNewSceneOrder((maxOrder + 1).toString());
  }, [showAddModal, editingSceneId, scenes]);

  const handleSave = async (): Promise<void> => {
    const orderNum = parseInt(newSceneOrder.trim(), 10);
    if (!newSceneOrder.trim() || isNaN(orderNum) || orderNum <= 0 || !newSceneTitle.trim() || addingScene || !documentId) return;
    const isEditing = editingSceneId !== null;
    try {
      setAddingScene(true);
      const url = isEditing
        ? (masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes/${editingSceneId}`)
        : (masterKey
            ? `${API_BASE}/api/documents/${documentId}/scenes?masterKey=${encodeURIComponent(masterKey)}`
            : `${API_BASE}/api/documents/${documentId}/scenes`);
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          order: orderNum,
          title: newSceneTitle.trim(),
          description: (newSceneDescription || '').trim() || undefined,
        }),
      });
      if (!response.ok) throw new Error(isEditing ? 'Failed to update scene' : 'Failed to create scene');

      if (!isEditing && newSceneStorylineIds.length > 0 && projectId && token) {
        const { scene: createdScene } = await response.json();
        await Promise.all(
          newSceneStorylineIds.map((storylineId) =>
            fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                sourceType: 'scene',
                sourceId: createdScene.id,
                sourceDocumentId: documentId,
                targetType: 'storyline',
                targetId: storylineId,
                targetDocumentId: documentId,
              }),
            })
          )
        );
      }

      await fetchScenes();
      await fetchEntityRefs();
      closeModal();
    } catch (err) {
      console.error(isEditing ? 'Error updating scene:' : 'Error creating scene:', err);
      setError(isEditing ? t('failed_to_update_scene') : t('failed_to_create_scene'));
    } finally {
      setAddingScene(false);
    }
  };

  const handleDelete = async (scene: Scene): Promise<void> => {
    if (!scene.id || deletingSceneId === scene.id || !documentId) return;
    try {
      setDeletingSceneId(scene.id);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) throw new Error('Failed to delete scene');
      if (editingSceneId === scene.id) {
        closeModal();
      }
      await fetchScenes();
      await fetchEntityRefs();
    } catch (err) {
      console.error('Error deleting scene:', err);
      setError(t('failed_to_delete_scene'));
    } finally {
      setDeletingSceneId(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (!over || active.id === over.id || !documentId || (!token && !masterKey)) return;
    const sorted = [...scenes].sort((a, b) => a.order - b.order);
    const oldIndex = sorted.findIndex((s) => s.id === active.id);
    const newIndex = sorted.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sorted, oldIndex, newIndex);
    try {
      for (let i = 0; i < reordered.length; i++) {
        const scene = reordered[i];
        const newOrder = i + 1;
        if (scene.order === newOrder) continue;
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/scenes/${scene.id}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ order: newOrder, title: scene.title, description: scene.description }),
        });
        if (!response.ok) throw new Error('Failed to update scene order');
      }
      setScenes(
        reordered.map((scene, i) => ({
          ...scene,
          order: i + 1,
        }))
      );
    } catch (err) {
      console.error('Error reordering scenes:', err);
      setError(t('failed_to_update_scene_order'));
    }
  };

  const timelineScenes: ScenesTimelineScene[] = useMemo(
    () => scenes.map((s) => ({ id: s.id, order: s.order, title: s.title, description: s.description })),
    [scenes]
  );

  if (loading) {
    if (isSidebar) {
      return (
        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
          {t('loading')}
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted-foreground)' }}>
        {t('loading')}
      </div>
    );
  }

  if (error) {
    if (isSidebar) {
      return (
        <div className="flex flex-col gap-2 p-4">
          <div className="text-sm text-red-600">{error}</div>
          <Button size="sm" variant="outline" onClick={() => fetchScenes()} className="w-full">
            {t('retry')}
          </Button>
        </div>
      );
    }
    return (
      <div>
        <PanelPageHeader
          onClose={onClose}
          closeAriaLabel={t('close')}
        />
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '3rem' }}>{error}</div>
      </div>
    );
  }

  const addSceneButton = (
    <AddItemButton onClick={() => setShowAddModal(true)} label={t('add_scene')} />
  );
  const scenesViewSwitcher = (
    <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'grid' | 'table' | 'timeline')}>
      <ViewSwitcher
        modes={['grid', 'table', 'timeline']}
        ariaLabelForMode={(mode) => (mode === 'timeline' ? t('Timeline') : t(mode))}
      />
    </Tabs>
  );

  const content = (
    <div className={PANEL_BODY_FILL_CLASSNAME}>
      <PanelHeaderActions
        isSidebar={isSidebar}
        onClose={onClose}
        closeAriaLabel={t('close')}
        hasItems={scenes.length > 0}
        viewSwitcher={scenesViewSwitcher}
      >
        {addSceneButton}
      </PanelHeaderActions>

      {scenes.length === 0 ? (
        <ScenesEmptyState canEdit onAdd={() => setShowAddModal(true)} />
      ) : (
        <div style={{ marginBottom: '2rem' }}>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'grid' | 'table' | 'timeline')}>
            <TabsContent value="grid">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={scenes.map((s) => s.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...scenes].sort((a, b) => a.order - b.order).map((scene, index) => (
                      <SortableSceneCard
                        key={scene.id}
                        scene={scene}
                        index={index}
                        onEdit={(s) => {
                          setEditingSceneId(s.id);
                          setNewSceneOrder(s.order.toString());
                          setNewSceneTitle(s.title);
                          setNewSceneDescription(s.description ?? '');
                          setShowAddModal(true);
                        }}
                        onDelete={(s) => setDeleteConfirmScene(s)}
                        deletingSceneId={deletingSceneId}
                        editingSceneId={editingSceneId}
                        token={token}
                        masterKey={masterKey}
                        documentId={documentId}
                        storylines={(storylineIdsByScene.get(scene.id) ?? [])
                          .map((id) => {
                            const name = storylinesById.get(id);
                            return name ? { id, name } : null;
                          })
                          .filter((storyline): storyline is { id: string; name: string } => Boolean(storyline))}
                        t={t}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => setShowAddModal(true)}
                      className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow flex flex-col items-center justify-center min-h-[140px] gap-2 text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="h-8 w-8" />
                      <span className="text-lg font-medium">{t('add_scene')}</span>
                    </button>
                  </div>
                </SortableContext>
              </DndContext>
            </TabsContent>
            <TabsContent value="table">
              <div
                style={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('title')}</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('description')}</th>
                        <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...scenes].sort((a, b) => a.order - b.order).map((scene, index) => (
                        <tr
                          key={scene.id}
                          style={{
                            borderBottom: index < scenes.length - 1 ? '1px solid var(--border)' : 'none',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--muted)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}
                        >
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--foreground)', fontWeight: 500 }}>{scene.title}</td>
                          <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--muted-foreground)', maxWidth: '500px' }}>
                            {scene.description ? (
                              <div
                                className={`line-clamp-2 ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
                                style={{ overflow: 'hidden', fontSize: '0.875rem', color: 'var(--muted-foreground)' }}
                                dangerouslySetInnerHTML={{ __html: scene.description }}
                              />
                            ) : (
                              '-'
                            )}
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setEditingSceneId(scene.id);
                                  setNewSceneOrder(scene.order.toString());
                                  setNewSceneTitle(scene.title);
                                  setNewSceneDescription(scene.description ?? '');
                                  setShowAddModal(true);
                                }}
                                disabled={editingSceneId === scene.id}
                                className="text-[var(--scribane-black)] hover:text-[var(--scribane-black)] hover:bg-gray-100"
                              >
                                <Pencil size={16} />
                              </Button>
                              <RotatingDeleteButton
                                onClick={() => setDeleteConfirmScene(scene)}
                                tooltipText={t('delete_scene')}
                                ariaLabel={t('delete_scene')}
                                disabled={deletingSceneId === scene.id}
                                className="h-8 w-8 p-0"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowAddModal(true)}
                        onKeyDown={(e) => e.key === 'Enter' && setShowAddModal(true)}
                        style={{ borderTop: '1px solid var(--border)', transition: 'background-color 0.2s', cursor: 'pointer' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--muted)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}
                      >
                        <td colSpan={3} style={{ padding: '1rem', fontSize: '0.875rem', color: '#9ca3af', textAlign: 'center', fontWeight: 500 }}>
                          <span className="inline-flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            {t('add_scene')}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="timeline">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                  <SingleSelectCombobox
                    options={[
                      { value: 'none', label: t('timeline_group_by_none') },
                      { value: 'chapter', label: t('scenes_timeline_group_by_chapter') },
                      { value: 'character', label: t('scenes_timeline_group_by_character') },
                      { value: 'location', label: t('scenes_timeline_group_by_location') },
                    ]}
                    value={sceneTimelineGroupBy}
                    onValueChange={(v) => {
                      setSceneTimelineGroupBy(v as 'none' | 'chapter' | 'character' | 'location');
                      if (v === 'chapter') setSceneTimelineGroupByChapters(new Set(chaptersForScenes.map((c) => c.id)));
                      if (v === 'character') setSceneTimelineGroupByCharacters(new Set(characters.map((c) => c.id)));
                      if (v === 'location') setSceneTimelineGroupByLocations(new Set(locations.map((l) => l.id)));
                    }}
                    placeholder={t('timeline_group_by')}
                    searchPlaceholder={t('timeline_filter_search_placeholder')}
                    emptyMessage={t('timeline_filter_no_results')}
                    align="start"
                  />
                  {sceneTimelineGroupBy === 'chapter' && (
                    <MultiSelectCombobox
                      options={chaptersForScenes.map((c) => ({ value: c.id, label: c.title }))}
                      selected={sceneTimelineGroupByChapters}
                      onSelectionChange={setSceneTimelineGroupByChapters}
                      label={t('scenes_timeline_group_by_chapters_label')}
                      searchPlaceholder={t('timeline_filter_search_placeholder')}
                      emptyMessage={t('timeline_filter_no_results')}
                      clearAllLabel={t('timeline_clear_all')}
                      align="start"
                    />
                  )}
                  {sceneTimelineGroupBy === 'character' && (
                    <MultiSelectCombobox
                      options={characters.map((c) => ({ value: c.id, label: c.name }))}
                      selected={sceneTimelineGroupByCharacters}
                      onSelectionChange={setSceneTimelineGroupByCharacters}
                      label={t('scenes_timeline_group_by_characters_label')}
                      searchPlaceholder={t('timeline_filter_search_placeholder')}
                      emptyMessage={t('timeline_filter_no_results')}
                      clearAllLabel={t('timeline_clear_all')}
                      align="start"
                    />
                  )}
                  {sceneTimelineGroupBy === 'location' && (
                    <MultiSelectCombobox
                      options={locations.map((l) => ({ value: l.id, label: l.name }))}
                      selected={sceneTimelineGroupByLocations}
                      onSelectionChange={setSceneTimelineGroupByLocations}
                      label={t('scenes_timeline_group_by_locations_label')}
                      searchPlaceholder={t('timeline_filter_search_placeholder')}
                      emptyMessage={t('timeline_filter_no_results')}
                      clearAllLabel={t('timeline_clear_all')}
                      align="start"
                    />
                  )}
                </div>
                <ScenesTimeline
                  scenes={timelineScenes}
                  groupBy={sceneTimelineGroupBy}
                  groupByChapters={sceneTimelineGroupByChapters}
                  groupByCharacters={sceneTimelineGroupByCharacters}
                  groupByLocations={sceneTimelineGroupByLocations}
                  entityRefs={entityRefs}
                  chapters={chaptersForScenes}
                  characters={characters}
                  locations={locations}
                  onSceneClick={(scene) => {
                    const full = scenes.find((s) => s.id === scene.id);
                    if (full) {
                      setEditingSceneId(full.id);
                      setNewSceneOrder(full.order.toString());
                      setNewSceneTitle(full.title);
                      setNewSceneDescription(full.description ?? '');
                      setShowAddModal(true);
                    }
                  }}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );

  const addModal = (
    <Dialog open={showAddModal} onOpenChange={(open) => !open && !addingScene && closeModal()}>
      <DialogContent className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}>
        <DialogTitle className="sr-only">{editingSceneId ? t('edit_scene') : t('add_scene')}</DialogTitle>
        <EntityDetailModalLayout
          header={
            <EntityDetailModalTitle>
              {editingSceneId ? t('edit_scene') : t('add_scene')}
            </EntityDetailModalTitle>
          }
          footer={
            <EditDialogFooter
              isEditing={editingSceneId !== null}
              isSaving={addingScene}
              saveDisabled={(() => {
                const orderNum = parseInt(newSceneOrder.trim(), 10);
                return !newSceneOrder.trim() || isNaN(orderNum) || orderNum <= 0 || !newSceneTitle.trim();
              })()}
              onCancel={closeModal}
              onSave={() => {
                void handleSave();
              }}
              onDelete={
                editingSceneId
                  ? () => {
                      const scene = scenes.find((s) => s.id === editingSceneId);
                      if (scene) setDeleteConfirmScene(scene);
                    }
                  : undefined
              }
              deleteDisabled={deletingSceneId === editingSceneId}
            />
          }
        >
          <div className="space-y-6 p-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                {t('title')} *
              </label>
              <Input
                type="text"
                value={newSceneTitle}
                onChange={(e) => setNewSceneTitle(e.target.value)}
                placeholder={t('scene_title_placeholder')}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t('description')}
              </label>
              <RichTextField
                value={newSceneDescription}
                onChange={setNewSceneDescription}
                placeholder={t('scene_description_placeholder')}
                minHeight="150px"
                mentionableEntities={mentionableEntities}
              />
            </div>
            <div>
              <EntityTagsField
                label={t('storylines')}
                entityType="storyline"
                options={storylineOptions}
                selectedIds={editingSceneId ? selectedStorylineIds : newSceneStorylineIds}
                onChange={editingSceneId ? handleStorylineTagsChange : setNewSceneStorylineIds}
                emptyHint={t('no_storylines')}
              />
            </div>
            {projectId && editingSceneId && (relatedEntities.linksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
              <div>
                <label className="mb-2 block text-sm font-semibold">
                  {t('related_entities')}
                </label>
                <div className="flex flex-col gap-3">
                  {relatedEntities.linksTo.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs text-muted-foreground">{t('links_to')}</span>
                      <div className="flex flex-wrap gap-2">
                        {relatedEntities.linksTo.map((e) => (
                          <RelatedEntityLink
                            key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                            entity={e}
                            projectId={projectId}
                            onRemove={() => handleRemoveLinksTo(e)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {relatedEntities.referencedIn.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs text-muted-foreground">{t('referenced_in')}</span>
                      <div className="flex flex-wrap gap-2">
                        {relatedEntities.referencedIn.map((e) => (
                          <RelatedEntityLink
                            key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                            entity={e}
                            projectId={projectId}
                            onRemove={() => handleRemoveReferencedIn(e)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </EntityDetailModalLayout>
      </DialogContent>
    </Dialog>
  );

  const deleteSceneDialog = (
    <ConfirmDialog
      open={deleteConfirmScene !== null}
      onOpenChange={(open) => !open && setDeleteConfirmScene(null)}
      title={t('delete_scene')}
      description={t('confirm_delete_scene')}
      confirmLabel={t('delete_scene')}
      onConfirm={() => {
        if (deleteConfirmScene) void handleDelete(deleteConfirmScene);
      }}
    />
  );

  if (isSidebar) {
    return (
      <>
        <div className={`${PANEL_BODY_FILL_CLASSNAME} gap-4 overflow-auto`} style={{ padding: '1rem' }}>
          {content}
        </div>
        {addModal}
        {deleteSceneDialog}
      </>
    );
  }

  return (
    <>
      {content}
      {addModal}
      {deleteSceneDialog}
    </>
  );
};
